import React, { useEffect, useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useGetSingleEmployee } from "../../hooks/useEmployees";
import { useCompany } from "../../context/CompanyContext";
import {
  n,
  isSameMonthYear,
  historyTotalForMonth,
  actualOrFull,
  storedTotalOr,
} from "../../utils/payrollValues";

const PayslipPDF = ({ data, id }) => {
  const company = useCompany();
  const payslipRef = useRef();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [logoRetry, setLogoRetry] = useState(0);
  useEffect(() => setLogoRetry(0), [company.logo]);
  const logoSrc = logoRetry > 0 && company.logo
    ? `${company.logo}${company.logo.includes('?') ? '&' : '?'}retry=${logoRetry}`
    : company.logo;
  const fmt = (v) => Number(v || 0).toFixed(2);

  // Mask a bank account number, showing only the last 4 digits, e.g.
  // "1234567891234" -> "XXXXXXXXX1234". Never throws on null/short values.
  const maskAccountNo = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const str = String(value).trim();
    if (!str) return 'N/A';
    if (str.length <= 4) return str;
    return 'X'.repeat(str.length - 4) + str.slice(-4);
  };

  // Format date to dd-mm-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getDaysInMonth = (monthName, year) => {
    if (!monthName || !year) return 30;
    const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
    return new Date(year, monthIndex + 1, 0).getDate();
  };

  // Binds this payslip's period to the shared matcher; the comparison itself lives
  // in utils/payrollValues so the unit tests exercise the shipped rule.
  const sameMonthYear = (item) => isSameMonthYear(item, data?.month, data?.year);

  const totalDaysInMonth = getDaysInMonth(data?.month, data?.year);
  const effectiveDays = n(data?.effective_work_days) || Math.max(totalDaysInMonth - n(data?.lop_days), 0);

  const baseItems = {
    basic: n(data?.full_basic || data?.basic),
    hra: n(data?.full_hra || data?.hra),
    conveyance: n(data?.full_conveyance || data?.conveyance),
    medical: n(data?.full_medical || data?.medical),
    special_allowance: n(data?.full_special_allowance || data?.special_allowance),
    food_allowance: n(data?.food_allowance), // auto: qualifying punch-in days × ₹70
    employer_pf: n(data?.full_employer_pf || data?.employer_pf), // Always show full Employer PF in Earnings
    travel_allowance: n(data?.travel_allowance),
    bonus: n(data?.bonus),
    recharge: n(data?.recharge),
    telephone_allowance: n(data?.telephone_allowance),
    exam_increment: n(data?.exam_increment),
    other_earnings: n(data?.other_earnings),
  };

  // ACTUAL column = the amounts payroll actually prorated for the period (join
  // date / LOP / partial days). These live in the plain fields (data.basic, ...)
  // that the payroll card and the stored net_pay already use. Fall back to the
  // full value only when a record genuinely lacks the prorated field (older /
  // imported payslips) — a nullish check, so a real ₹0 is respected. This replaces
  // the old "actual = full" shortcut that made mid-month joiners / LOP show a full
  // month's pay on the PDF while the card correctly showed the prorated amount.
  // The resolution rule itself is actualOrFull() in utils/payrollValues, so the
  // unit tests bind to the shipped code rather than to a copy.
  const act = (actualKey, fullKey) => actualOrFull(data, actualKey, fullKey);
  const actualItems = {
    basic: act('basic', 'full_basic'),
    hra: act('hra', 'full_hra'),
    conveyance: act('conveyance', 'full_conveyance'),
    medical: act('medical', 'full_medical'),
    special_allowance: act('special_allowance', 'full_special_allowance'),
    food_allowance: n(data?.food_allowance), // inherently actual (punch-based)
    employer_pf: act('employer_pf', 'full_employer_pf'),
    // The components below have no full_* column on Payslip: payroll stores only
    // the already-prorated amount (_calculate_salary saves paid_travel_allowance
    // etc.), so Base and Actual are deliberately the same figure. act() here would
    // read a field that does not exist and return exactly this value anyway.
    travel_allowance: n(data?.travel_allowance),
    bonus: n(data?.bonus),
    recharge: n(data?.recharge),
    telephone_allowance: n(data?.telephone_allowance),
    exam_increment: n(data?.exam_increment),
    other_earnings: n(data?.other_earnings),
  };

  // Employer PF: the fallback to the earnings column is deliberate and load-bearing.
  // Payslip.employer_pf_deduction is NOT NULL, and 173 payslips store 0 there while
  // employer_pf carries the real 1,800 - treating that 0 as a real value would drop
  // 1,800 from their deduction lines and stop the payslip balancing (checked on live
  // data for CRM #48343; the underlying column gap is the separate issue on #48340).
  const employerPF = n(data?.employer_pf_deduction || data?.employer_pf);
  const lopDeduction = n(data?.lop_deduction || 0);
  const fullBaseTotal = Object.values(baseItems).reduce((s, v) => s + n(v), 0);
  const actualBaseTotal = Object.values(actualItems).reduce((s, v) => s + n(v), 0);

  // A month that HAS history rows wins even when they add up to zero; the flat
  // field is used only when this month has no history row at all. (`|| flat` was
  // wrong: a genuine zero for the month fell through to the sticky amount.) The
  // summing rule is historyTotalForMonth() in utils/payrollValues — one definition,
  // covered by the unit tests.
  const monthlyHistoryTotal = (rows, key) => historyTotalForMonth(rows, key, data?.month, data?.year);

  const incentiveFromHistory = monthlyHistoryTotal(data?.incentive_history, 'incentive_amount');
  const incentiveAmount = incentiveFromHistory === null ? n(data?.incentive) : incentiveFromHistory;

  const fullComputedEarnings = fullBaseTotal + incentiveAmount;
  const actualComputedEarnings = actualBaseTotal + incentiveAmount;

  // Stored totals take precedence to match the database and backend-generated PDF
  // exactly. storedTotalOr() in utils/payrollValues carries the rule (a stored 0 is
  // honoured), so the tests cover the same code the PDF runs. full_total_earnings
  // uses requirePositive because 0 there means the column was never populated on
  // that payslip, not a genuine zero month.
  const actualTotalEarnings = storedTotalOr(data?.total_earnings, actualComputedEarnings);
  const fullTotalEarnings = storedTotalOr(data?.full_total_earnings, fullComputedEarnings, { requirePositive: true });

  const pf = n(data?.pf);
  const professionalTax = n(data?.professional_tax);
  const tdsFromHistory = monthlyHistoryTotal(data?.tds_history, 'tds_amount');
  const tdsAmount = tdsFromHistory === null ? n(data?.income_tax) : tdsFromHistory;

  const loansFromHistory = (data?.deduction_history || [])
    .filter(sameMonthYear)
    .reduce((s, it) => s + n(it?.loan), 0);
  const otherFromHistory = (data?.deduction_history || [])
    .filter(sameMonthYear)
    .reduce((s, it) => s + n(it?.other_deduction), 0);

  // Same reason as Employer PF above: Payslip.loan is NOT NULL (0 on 2,645 of 2,714
  // payslips), so a stored 0 cannot be told apart from "no loan column written" and the
  // deduction-history fallback is what surfaces a loan recorded for the month.
  const topLevelLoan = n(data?.loan);
  const finalLoan = topLevelLoan > 0 ? topLevelLoan : loansFromHistory;

  const topLevelStaffAdvance = n(data?.staff_advance);

  const topLevelOtherDeduction = n(data?.other_deduction);
  const finalOtherDeduction = topLevelOtherDeduction > 0 ? topLevelOtherDeduction : otherFromHistory;

  const esi = n(data?.esi);
  const penalty = n(data?.penalty);

  const computedDeductions = pf + professionalTax + tdsAmount + finalLoan + topLevelStaffAdvance + finalOtherDeduction + lopDeduction + employerPF + esi + penalty;

  const totalDeductions = storedTotalOr(data?.total_deductions, computedDeductions);
  const netPay = storedTotalOr(data?.net_pay, actualTotalEarnings - totalDeductions);

  const { data: emp } = useGetSingleEmployee({ id: data?.employee_no || id });
  const employee = emp?.employee;

  const downloadPayslip = () => {
    const input = payslipRef.current;
    if (!input) {
      console.error("Payslip reference is not available");
      return;
    }

    setIsGeneratingPDF(true);
    
    // Scroll to top to ensure html2canvas starts from the beginning
    window.scrollTo(0, 0);

    setTimeout(() => {
      const input = payslipRef.current;
      if (!input) {
        setIsGeneratingPDF(false);
        return;
      }

      html2canvas(input, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: "#ffffff",
        onclone: (clonedDoc) => {
          const clonedInput = clonedDoc.querySelector('.payslip-container');
          if (clonedInput) {
            clonedInput.style.width = '794px';
            clonedInput.style.height = 'auto';
            clonedInput.style.minHeight = 'auto';
            clonedInput.style.padding = '30px'; 
            clonedInput.style.boxSizing = 'border-box';
          }
        }
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/jpeg", 0.95);
        const pdf = new jsPDF("p", "mm", "a4");
        
        const pageWidthMm = 210;
        const pageHeightMm = 297;
        const marginMm = 10; // Standard margin
        
        const contentWidthMm = pageWidthMm - (marginMm * 2);
        const contentHeightMm = (canvas.height * contentWidthMm) / canvas.width;
        
        // Scale down if it exceeds a single A4 page height
        let finalWidth = contentWidthMm;
        let finalHeight = contentHeightMm;
        
        if (finalHeight > (pageHeightMm - (marginMm * 2))) {
          finalHeight = pageHeightMm - (marginMm * 2);
          finalWidth = (canvas.width * finalHeight) / canvas.height;
        }

        // Center on page
        const xOffset = (pageWidthMm - finalWidth) / 2;
        const yOffset = 10; // Start near top

        pdf.addImage(imgData, "JPEG", xOffset, yOffset, finalWidth, finalHeight);

        const safeName = (employee?.name || 'Employee').trim().replace(/[^a-zA-Z0-9]/g, '_');
        pdf.save(`Payslip_${safeName}_${data?.month}_${data?.year}.pdf`);
        setIsGeneratingPDF(false);
      }).catch(err => {
        console.error("PDF Generation Error:", err);
        setIsGeneratingPDF(false);
      });
    }, 800);
  };

  const getContainerStyles = () => {
    if (isGeneratingPDF) {
      return {
        background: "#ffffff",
        width: "794px",
        maxWidth: "794px",
        margin: "0",
        padding: "20px 40px",
        boxShadow: "none",
        borderRadius: "0"
      };
    }
    return {
      background: "#ffffff",
      width: "100%",
      // Keep the real document width so the layout stays PDF-accurate; on phones
      // the parent scrolls horizontally rather than collapsing the columns.
      minWidth: "700px",
      maxWidth: "800px",
      margin: "0 auto",
      padding: "20px 40px",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
      borderRadius: "4px"
    };
  };

  return (
    <div>
      {data ? (
        <div style={{
          padding: isGeneratingPDF ? "0" : "20px",
          background: "#fff",
          minHeight: isGeneratingPDF ? "auto" : "100vh",
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
          // On narrow screens the fixed-layout payslip keeps its real document
          // width and scrolls horizontally — like viewing a PDF page — instead of
          // squishing the table cells into one-character-per-line columns.
          overflowX: isGeneratingPDF ? "visible" : "auto",
          WebkitOverflowScrolling: "touch",
        }}>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
          <style>
            {`
              @media print {
                body { margin: 0; }
                .no-print { display: none; }
              }
              
              .payslip-container {
                color: #000000;
                background: #ffffff;
                width: 100%;
                padding: ${isGeneratingPDF ? '30px 40px' : '25px'};
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                min-height: auto;
              }

              .outer-table {
                width: 100%;
                border-collapse: collapse;
                table-layout: fixed;
                border: none;
              }

              .payslip-table {
                width: 100%;
                border-collapse: collapse;
                table-layout: fixed;
                border: 1px solid #1b6d24;
              }
              
              .payslip-table th,
              .payslip-table td {
                padding: ${isGeneratingPDF ? '12px 15px' : '14px 18px'};
                text-align: left;
                border: 1px solid #c1c7cd;
                font-size: ${isGeneratingPDF ? '12px' : '14px'};
                line-height: 1.5;
                overflow: hidden;
                word-wrap: break-word;
                color: #000000;
              }
              
              .payslip-table th {
                background-color: #ffffff;
                font-weight: 900;
                color: #1b6d24;
                text-transform: uppercase;
                letter-spacing: 1px;
                border: 1px solid #1b6d24;
                padding: ${isGeneratingPDF ? '12px 18px' : '14px 20px'};
              }
              
              .section-header-bar {
                background-color: #ffffff;
                color: #1b6d24;
                padding: ${isGeneratingPDF ? '10px 15px' : '16px 24px'};
                font-weight: 950;
                font-size: ${isGeneratingPDF ? '14px' : '16px'};
                text-align: left;
                border: 1px solid #1b6d24;
                border-bottom: 2px solid #1b6d24;
                letter-spacing: 1.5px;
                text-transform: uppercase;
                margin-bottom: 0;
              }
              
              .total-row {
                background-color: #ffffff;
                font-weight: 700;
                border: 1px solid #1b6d24;
              }
              
              .amount-cell {
                text-align: right;
                font-variant-numeric: tabular-nums;
                font-weight: 600;
                white-space: nowrap;
              }
              
              .info-label {
                font-weight: 800;
                color: #374151;
                text-transform: uppercase;
                font-size: ${isGeneratingPDF ? '10px' : '12px'};
                letter-spacing: 1px;
                display: block;
              }
              
              .info-value {
                color: #000000;
                font-weight: 600;
                display: block;
              }
              
              .net-pay-box {
                background-color: #ffffff;
                border: 1px solid #1b6d24;
                border-top: 3px solid #1b6d24;
                margin-top: 10px;
                width: 100%;
              }

              @media screen and (max-width: 768px) {
                .payslip-container:not(.pdf-generating) {
                  padding: 10px !important;
                }
              }
            `}
          </style>

          <div
            ref={payslipRef}
            className={`payslip-container ${isGeneratingPDF ? 'pdf-generating' : ''}`}
            style={getContainerStyles()}
          >
            {/* Premium Header Table */}
            <table className="outer-table" style={{ 
              marginBottom: isGeneratingPDF ? "25px" : "40px", 
              borderBottom: '1px solid #e0e3e5',
              paddingBottom: '10px'
            }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'middle', width: '40%', padding: '10px' }}>
                    {company.logo && logoRetry < 3 ? (
                      <img
                        src={logoSrc}
                        alt={`${company.name || 'Company'} Logo`}
                        crossOrigin="anonymous"
                        onError={() => setLogoRetry((attempt) => Math.min(attempt + 1, 3))}
                        style={{
                          maxHeight: isGeneratingPDF ? "62px" : "82px",
                          maxWidth: isGeneratingPDF ? '210px' : '230px',
                          width: 'auto',
                          height: 'auto',
                          display: 'block',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <div style={{ fontSize: isGeneratingPDF ? '24px' : '30px', fontWeight: 900, color: company.primaryColor || '#1976d2' }}>
                        {company.shortName || company.name || company.code || 'HR'}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', verticalAlign: 'middle', padding: '10px' }}>
                    <div style={{ marginBottom: '5px' }}>
                      <span style={{
                        fontSize: isGeneratingPDF ? '18px' : '22px',
                        fontWeight: '950',
                        color: '#1b6d24',
                        letterSpacing: '1px',
                        textTransform: 'uppercase'
                      }}>
                        PAYSLIP {data?.month?.toUpperCase()} {data?.year}
                      </span>
                    </div>
                    <p style={{
                      fontSize: isGeneratingPDF ? '15px' : '16px',
                      color: '#000',
                      margin: '0',
                      fontWeight: '850'
                    }}>
                      {company.legalName || company.name || 'Company'}
                    </p>
                    <p style={{
                      fontSize: isGeneratingPDF ? '10px' : '11px',
                      color: '#444',
                      margin: '5px 0 0 0',
                      lineHeight: '1.4'
                    }}>
                      <span style={{ whiteSpace: 'pre-line' }}>{company.address || ''}</span>
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Employee Details Table */}
            <div style={{ marginBottom: isGeneratingPDF ? "40px" : "40px" }}>
              <div className="section-header-bar">
                EMPLOYEE INFORMATION
              </div>
              <table className="payslip-table" style={{ borderTop: 'none' }}>
                <tbody>
                  <tr>
                    <td style={{ width: '25%' }}><span className="info-label">EMPLOYEE NAME:</span></td>
                    <td style={{ width: '25%' }}><span className="info-value">{employee?.name || 'N/A'}</span></td>
                    <td style={{ width: '25%' }}><span className="info-label">BANK ACCOUNT NO:</span></td>
                    <td style={{ width: '25%' }}><span className="info-value">{maskAccountNo(employee?.bank_account_no)}</span></td>
                  </tr>
                  <tr>
                    <td><span className="info-label">EMPLOYEE ID:</span></td>
                    <td><span className="info-value">{data?.employee_no || id || 'N/A'}</span></td>
                    <td><span className="info-label">UAN NUMBER:</span></td>
                    <td><span className="info-value">{employee?.uan_no || 'N/A'}</span></td>
                  </tr>
                  <tr>
                    <td><span className="info-label">DESIGNATION:</span></td>
                    <td><span className="info-value">{employee?.designation || 'N/A'}</span></td>
                    <td><span className="info-label">PAN NUMBER:</span></td>
                    <td><span className="info-value">{employee?.pan_no || 'N/A'}</span></td>
                  </tr>
                  <tr>
                    <td><span className="info-label">DEPARTMENT:</span></td>
                    <td><span className="info-value">{employee?.department || 'N/A'}</span></td>
                    <td><span className="info-label">DATE OF JOINING:</span></td>
                    <td><span className="info-value">{formatDate(employee?.date_of_joining)}</span></td>
                  </tr>
                  <tr>
                    <td><span className="info-label">TOTAL WORK DAYS:</span></td>
                    <td><span className="info-value">{totalDaysInMonth} Days</span></td>
                    <td><span className="info-label">EFFECTIVE DAYS:</span></td>
                    <td><span className="info-value">{data?.effective_work_days || effectiveDays} Days</span></td>
                  </tr>
                  <tr>
                    <td><span className="info-label">LOP DAYS:</span></td>
                    <td><span className="info-value" style={{color: data?.lop_days > 0 ? '#d32f2f' : 'inherit'}}>{data?.lop_days || 0} Days</span></td>
                    <td><span className="info-label">LEAVE DAYS:</span></td>
                    <td><span className="info-value">{data?.leave_days || 0} Days</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Earnings and Deductions Side-by-Side Table */}
            <table className="outer-table" style={{ marginBottom: isGeneratingPDF ? "50px" : "35px" }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top', paddingRight: '10px' }}>
                    <div style={{ color: '#1b6d24', fontWeight: '950', fontSize: '13px', marginBottom: '8px', letterSpacing: '1px' }}>EARNINGS</div>
                    <table className="payslip-table">
                      <thead>
                        <tr style={{ backgroundColor: '#ffffff' }}>
                          <th style={{ width: '45%', fontSize: '11px', color: '#000' }}>PARTICULARS</th>
                          <th style={{ width: '25%', fontSize: '11px', color: '#000' }} className="amount-cell">FULL [₹]</th>
                          <th style={{ width: '30%', fontSize: '11px', color: '#000' }} className="amount-cell">ACTUAL [₹]</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td>Basic Salary</td><td className="amount-cell">{fmt(baseItems.basic)}</td><td className="amount-cell">{fmt(actualItems.basic)}</td></tr>
                        <tr><td>House Rent Allowance (HRA)</td><td className="amount-cell">{fmt(baseItems.hra)}</td><td className="amount-cell">{fmt(actualItems.hra)}</td></tr>
                        <tr><td>Conveyance Allowance</td><td className="amount-cell">{fmt(baseItems.conveyance)}</td><td className="amount-cell">{fmt(actualItems.conveyance)}</td></tr>
                        <tr><td>Medical Allowance</td><td className="amount-cell">{fmt(baseItems.medical)}</td><td className="amount-cell">{fmt(actualItems.medical)}</td></tr>
                        <tr><td>Special Allowance</td><td className="amount-cell">{fmt(baseItems.special_allowance)}</td><td className="amount-cell">{fmt(actualItems.special_allowance)}</td></tr>
                        {/* Non-HO employees get no food allowance; hide the line entirely when 0 (CRM #44163). */}
                        {(Number(baseItems.food_allowance) > 0 || Number(actualItems.food_allowance) > 0) && (
                          <tr><td>Food Allowance</td><td className="amount-cell">{fmt(baseItems.food_allowance)}</td><td className="amount-cell">{fmt(actualItems.food_allowance)}</td></tr>
                        )}
                        {(Number(baseItems.travel_allowance) > 0 || Number(actualItems.travel_allowance) > 0) && (
                          <tr><td>Travel Allowance</td><td className="amount-cell">{fmt(baseItems.travel_allowance)}</td><td className="amount-cell">{fmt(actualItems.travel_allowance)}</td></tr>
                        )}
                        {(Number(baseItems.bonus) > 0 || Number(actualItems.bonus) > 0) && (
                          <tr><td>Bonus</td><td className="amount-cell">{fmt(baseItems.bonus)}</td><td className="amount-cell">{fmt(actualItems.bonus)}</td></tr>
                        )}
                        {(Number(baseItems.recharge) > 0 || Number(actualItems.recharge) > 0) && (
                          <tr><td>Recharge</td><td className="amount-cell">{fmt(baseItems.recharge)}</td><td className="amount-cell">{fmt(actualItems.recharge)}</td></tr>
                        )}
                        {(Number(baseItems.telephone_allowance) > 0 || Number(actualItems.telephone_allowance) > 0) && (
                          <tr><td>Telephone Allowance</td><td className="amount-cell">{fmt(baseItems.telephone_allowance)}</td><td className="amount-cell">{fmt(actualItems.telephone_allowance)}</td></tr>
                        )}
                        {(Number(baseItems.exam_increment) > 0 || Number(actualItems.exam_increment) > 0) && (
                          <tr><td>Exam Increment</td><td className="amount-cell">{fmt(baseItems.exam_increment)}</td><td className="amount-cell">{fmt(actualItems.exam_increment)}</td></tr>
                        )}
                        {(Number(baseItems.other_earnings) > 0 || Number(actualItems.other_earnings) > 0) && (
                          <tr><td>Other Earnings</td><td className="amount-cell">{fmt(baseItems.other_earnings)}</td><td className="amount-cell">{fmt(actualItems.other_earnings)}</td></tr>
                        )}
                        {baseItems.employer_pf > 0 && <tr><td>Employer Provident Fund</td><td className="amount-cell">{fmt(baseItems.employer_pf)}</td><td className="amount-cell">{fmt(actualItems.employer_pf)}</td></tr>}
                        {incentiveAmount > 0 && <tr><td>Incentive</td><td className="amount-cell">{fmt(incentiveAmount)}</td><td className="amount-cell">{fmt(incentiveAmount)}</td></tr>}
                        <tr className="total-row" style={{ borderTop: '2px solid #1b6d24' }}>
                          <td><strong>TOTAL EARNINGS</strong></td>
                          <td className="amount-cell"><strong>{fmt(fullTotalEarnings)}</strong></td>
                          <td className="amount-cell"><strong>{fmt(actualTotalEarnings)}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                  <td style={{ verticalAlign: 'top', paddingLeft: '10px' }}>
                    <div style={{ color: '#1b6d24', fontWeight: '950', fontSize: '13px', marginBottom: '8px', letterSpacing: '1px' }}>DEDUCTIONS</div>
                    <table className="payslip-table">
                      <thead>
                        <tr style={{ backgroundColor: '#ffffff' }}>
                          <th style={{ width: '60%', fontSize: '11px', color: '#000' }}>PARTICULARS</th>
                          <th style={{ width: '40%', fontSize: '11px', color: '#000' }} className="amount-cell">AMOUNT [₹]</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td>Provident Fund (PF)</td><td className="amount-cell">{fmt(pf)}</td></tr>
                        <tr><td>Insurance</td><td className="amount-cell">{fmt(professionalTax)}</td></tr>
                        {lopDeduction > 0 && <tr><td>LOP</td><td className="amount-cell" style={{color: '#d32f2f'}}>{fmt(lopDeduction)}</td></tr>}
                        {finalLoan > 0 && <tr><td>Loan</td><td className="amount-cell">{fmt(finalLoan)}</td></tr>}
                        {topLevelStaffAdvance > 0 && <tr><td>Staff Advance</td><td className="amount-cell">{fmt(topLevelStaffAdvance)}</td></tr>}
                        {tdsAmount > 0 && <tr><td>Income Tax (TDS)</td><td className="amount-cell">{fmt(tdsAmount)}</td></tr>}
                        {esi > 0 && <tr><td>ESI</td><td className="amount-cell">{fmt(esi)}</td></tr>}
                        {finalOtherDeduction > 0 && <tr><td>Other</td><td className="amount-cell">{fmt(finalOtherDeduction)}</td></tr>}
                        {penalty > 0 && <tr><td>Penalty</td><td className="amount-cell">{fmt(penalty)}</td></tr>}
                        {employerPF > 0 && <tr><td>Employer Provident Fund</td><td className="amount-cell">{fmt(employerPF)}</td></tr>}
                        <tr className="total-row" style={{ borderTop: '2px solid #1b6d24' }}>
                          <td><strong>TOTAL DEDUCTIONS</strong></td>
                          <td className="amount-cell"><strong>{fmt(totalDeductions)}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Net Pay Box */}
            <div className="net-pay-box" style={{ marginBottom: isGeneratingPDF ? "30px" : "45px" }}>
              <table className="outer-table" style={{ border: 'none' }}>
                <tbody>
                  <tr>
                    <td style={{
                      padding: isGeneratingPDF ? '30px 40px' : '15px 25px',
                      fontSize: isGeneratingPDF ? '18px' : '16px',
                      fontWeight: '950',
                      color: '#1b6d24',
                      textTransform: 'uppercase',
                      letterSpacing: '3px',
                      width: '55%'
                    }}>
                      Net Salary Payable
                    </td>
                    <td className="amount-cell" style={{
                      padding: isGeneratingPDF ? '30px 40px' : '15px 25px',
                      fontSize: isGeneratingPDF ? '32px' : '26px',
                      fontWeight: '1000',
                      color: '#1b6d24',
                      textAlign: 'right'
                    }}>
                      ₹ {fmt(netPay)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ flexGrow: isGeneratingPDF ? 1 : 0 }}></div>

            {/* Footer */}
            <div style={{
              marginTop: isGeneratingPDF ? "40px" : "25px",
              paddingTop: isGeneratingPDF ? "25px" : "15px",
              borderTop: "3px solid #e0e3e5",
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: isGeneratingPDF ? '12px' : '11px',
                color: '#111',
                fontWeight: '600',
                margin: '0 0 8px 0'
              }}>
                This is a system-generated payslip and does not require a physical signature.
              </p>
              <p style={{
                fontSize: isGeneratingPDF ? '10px' : '10px',
                color: '#666',
                margin: 0
              }}>
                For any queries, please contact {company.legalName || company.name || 'the company'} HR.
                {company.registrationNumber ? ` | ${company.registrationNumber}` : ''}
              </p>
            </div>

            {/* Company Seal/Signature Table */}
            <table className="outer-table" style={{ marginTop: isGeneratingPDF ? "30px" : "60px", marginBottom: "0" }}>
              <tbody>
                <tr>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{
                      borderTop: '2px solid #000',
                      paddingTop: '15px',
                      width: '240px',
                      margin: '0 auto',
                      fontSize: isGeneratingPDF ? '13px' : '13px',
                      fontWeight: '800',
                      color: '#000',
                      letterSpacing: '1px'
                    }}>
                      Employee Signature
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{
                      borderTop: '2px solid #000',
                      paddingTop: '15px',
                      width: '240px',
                      margin: '0 auto',
                      fontSize: isGeneratingPDF ? '13px' : '13px',
                      fontWeight: '800',
                      color: '#000',
                      letterSpacing: '1px'
                    }}>
                      Authorized Signatory
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Download Button */}
          {!isGeneratingPDF && (
            <button
              onClick={downloadPayslip}
              className="no-print"
              style={{
                marginTop: "30px",
                display: "block",
                width: "220px",
                padding: "14px 24px",
                background: "#000",
                color: "#fff",
                border: "2px solid #000",
                borderRadius: "4px",
                cursor: "pointer",
                margin: "30px auto",
                fontSize: "14px",
                fontWeight: 700,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#fff";
                e.target.style.color = "#000";
                e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "#000";
                e.target.style.color = "#fff";
                e.target.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.3)";
              }}
            >
              {isGeneratingPDF ? 'Generating PDF...' : 'Download Payslip'}
            </button>
          )}
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          fontSize: '16px',
          color: '#6c757d'
        }}>
          Loading payslip data...
        </div>
      )}
    </div>
  );
};

export default PayslipPDF;
